/*
 * Copyright (c) 2024-2026. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, { useState } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs'
import relativeTime from 'dayjs/plugin/relativeTime'
import { ArrowLeftIcon, BellIcon, ArrowTopRightOnSquareIcon, CheckIcon } from '@heroicons/react/24/outline'
import { Notification } from 'types/entities/Notification'
import i18n from 'i18next'
import { getReminderModelConfig } from 'utils/reminders/reminderUtils'
import { openEntityByModelName } from 'utils/reminders/openEntityByModelName'
import { useAppDispatch, AppDispatch } from 'helpers/store'
import { getNotificationLinkRef } from 'utils/notifications/notificationEntity'
import { RemindersAction } from 'store/reminders/RemindersAction'
import { toast } from 'utils/toastUtils'

dayjs.extend(relativeTime)

interface UiNotificationDetailPanelProps {
    notification: Notification
    onBack: () => void
    onMarkRead: (notification: Notification) => void
    onNavigate: (path: string) => void
    onClose?: () => void
    t: (key: string) => string
}

const UiNotificationDetailPanel: React.FC<UiNotificationDetailPanelProps> = ({
    notification,
    onBack,
    onMarkRead,
    onNavigate,
    onClose,
    t,
}) => {
    const dispatch = useAppDispatch()
    const [isOpeningEntity, setIsOpeningEntity] = useState(false)
    const isUnread = !notification.read_at
    const linkRef = getNotificationLinkRef(notification)
    const modelName = linkRef.entity?.modelName ?? null
    const modelId = linkRef.entity?.modelId ?? null
    const entityDisplayName = linkRef.entity?.label ?? null
    const reminderId = linkRef.reminderId
    const modelConfig = modelName ? getReminderModelConfig(modelName) : null
    const canOpenEntity = Boolean((modelName && modelId) || reminderId)

    const handleGoToEntity = async () => {
        if (!canOpenEntity || isOpeningEntity) return
        onMarkRead(notification)
        setIsOpeningEntity(true)
        try {
            let nextModelName = modelName
            let nextModelId = modelId

            if ((!nextModelName || !nextModelId) && reminderId) {
                const reminder = await dispatch(
                    RemindersAction.fetchReminderDetail(reminderId)
                )
                nextModelName = reminder.model_name
                nextModelId = reminder.model_id
            }

            if (!nextModelName || nextModelId == null) {
                toast.error(t('No linked entity found') as string)
                return
            }

            const opened = await openEntityByModelName({
                dispatch: dispatch as unknown as AppDispatch,
                t: t as any,
                navigate: (path) => {
                    onClose?.()
                    onNavigate(path)
                },
                modelName: nextModelName,
                modelId: nextModelId,
            })
            if (opened) {
                onClose?.()
            }
        } catch {
            toast.error(t('An error occurred') as string)
        } finally {
            setIsOpeningEntity(false)
        }
    }

    const handleMarkRead = () => {
        onMarkRead(notification)
        onBack()
    }

    const entityLabel = modelName && modelId
        ? entityDisplayName
            ? `${t(modelConfig?.label ?? modelName)}: ${entityDisplayName}`
            : `${t(modelConfig?.label ?? modelName)} #${modelId}`
        : reminderId
          ? `${t('Reminder')} #${reminderId}`
          : null

    return (
        <div className={clsx('UiNotificationDetailPanel flex flex-col')}>
            <div className={clsx('flex items-center gap-2 px-4 py-3 border-b border-[var(--color-border)] shrink-0')}>
                <button
                    type="button"
                    onClick={onBack}
                    className={clsx(
                        'p-1 rounded-md transition-colors text-gray-500 hover:text-gray-900 hover:bg-gray-100',
                        'dark:text-gray-400 dark:hover:text-gray-100 dark:hover:bg-gray-800'
                    )}
                    title={t('Back')}
                >
                    <ArrowLeftIcon className={clsx('size-4')} />
                </button>
                <span className={clsx('text-sm font-semibold uppercase tracking-wide flex-1')}>
                    {t('Notification Detail')}
                </span>
                {isUnread && (
                    <div className={clsx('h-2 w-2 rounded-full bg-[var(--color-alert-info)] shrink-0')} />
                )}
            </div>

            <div className={clsx('flex flex-col gap-4 px-4 py-4 overflow-y-auto custom-scrollbar')}>
                <div className={clsx('flex flex-col gap-1')}>
                    <span className={clsx('text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium')}>
                        {t('Message')}
                    </span>
                    <p className={clsx('text-sm text-[var(--color-text)] leading-relaxed break-words')}>
                        {notification.data?.message || t('No message')}
                    </p>
                </div>

                {notification.data?.sender && (
                    <div className={clsx('flex flex-col gap-1')}>
                        <span className={clsx('text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium')}>
                            {t('From')}
                        </span>
                        <span className={clsx('text-sm text-[var(--color-text)]')}>
                            {notification.data.sender}
                        </span>
                    </div>
                )}

                <div className={clsx('flex flex-col gap-1')}>
                    <span className={clsx('text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium')}>
                        {t('When')}
                    </span>
                    <span className={clsx('text-sm text-[var(--color-text)]')}>
                        {dayjs(notification.created_at).locale(i18n.language).format('DD.MM.YYYY HH:mm')}
                        <span className={clsx('ml-2 text-xs text-gray-400 dark:text-gray-500')}>
                            ({dayjs(notification.created_at).locale(i18n.language).fromNow()})
                        </span>
                    </span>
                </div>

                {canOpenEntity && entityLabel && (
                    <div className={clsx('flex flex-col gap-1')}>
                        <span className={clsx('text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium')}>
                            {t('Attached to')}
                        </span>
                        <button
                            type="button"
                            onClick={() => void handleGoToEntity()}
                            disabled={isOpeningEntity}
                            className={clsx(
                                'flex items-center gap-2 self-start px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                                modelConfig
                                    ? [modelConfig.colorClass, modelConfig.textColorClass]
                                    : 'bg-teal-50 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300'
                            )}
                        >
                            <BellIcon className={clsx('size-4 shrink-0')} />
                            <span>{entityLabel}</span>
                            <ArrowTopRightOnSquareIcon className={clsx('size-3.5 shrink-0 opacity-70')} />
                        </button>
                    </div>
                )}
            </div>

            <div className={clsx('px-4 py-3 border-t border-[var(--color-border)] shrink-0 flex flex-col gap-2')}>
                {canOpenEntity && (
                    <button
                        type="button"
                        onClick={() => void handleGoToEntity()}
                        disabled={isOpeningEntity}
                        className={clsx(
                            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                            'bg-[var(--color-primary)] text-white hover:opacity-90 disabled:opacity-60'
                        )}
                    >
                        <ArrowTopRightOnSquareIcon className={clsx('size-4')} />
                        {isOpeningEntity ? t('Opening...') : t('Open entity')}
                    </button>
                )}
                {isUnread && (
                    <button
                        type="button"
                        onClick={handleMarkRead}
                        className={clsx(
                            'w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors',
                            canOpenEntity
                                ? 'bg-transparent border border-[var(--color-border)] text-[var(--color-text)] hover:bg-[var(--color-hover)]'
                                : 'bg-[var(--color-primary)] text-white hover:opacity-90'
                        )}
                    >
                        <CheckIcon className={clsx('size-4')} />
                        {t('Mark as read')}
                    </button>
                )}
            </div>
        </div>
    )
}

export default UiNotificationDetailPanel
